import { formatDateToLocalStr } from '../../../utils/format/formatDate';
import { getContentPromotionGoalsSettings } from '../../../utils/getContentPromotionGoalsSettings';
import { useExperimentFlag } from '../../../utils/useExperimentFlag';
import { useTranslation } from '../../../utils/useTranslation';
import { WithHint } from '../../feedback/Tooltip';
import { Flex } from '../../layout/Flex';
import { Price } from '../../price/Price';
import { NumberTypography } from '../../price/styles';
import { Typography } from '../../typography/Typography';
import { POSTS_TEXT_MAPPING } from './constants';
import { Wrapper } from './styles';
import { getGoalCostText } from './utils/getGoalCostText';
import { getStrategyText } from './utils/getStrategyText';
import type { ContentPromotionStatsProps } from './types';

export const ContentPromotionStats: React.FC<ContentPromotionStatsProps> = ({
    login,
    postsValue,
    goals,
    selectedGoals,
    weeklyLimit,
    regions,
    date,
    strategyType,
    spendLimitType,
    payFor,
    ...other
}) => {
    const t = useTranslation();

    const isStrategyEnabled = Number(useExperimentFlag('content_promotion_strategy')) === 1;

    const selectedGoalsIds = selectedGoals && Object.keys(selectedGoals);
    const selected =
        goals && selectedGoalsIds
            ? goals.filter((goal) => selectedGoalsIds.indexOf(goal.id) !== -1)
            : undefined;

    const regionsValues = regions && Object.values(regions);

    const strategyText = getStrategyText({ strategyType, payFor });
    const { showCostType } = isStrategyEnabled
        ? getContentPromotionGoalsSettings(selectedGoals, strategyType, spendLimitType)
        : { showCostType: false };

    return (
        <Wrapper {...other}>
            <Typography variant="title3" weight="bold" pb={2}>
                {t('Сведения о кампании')}
            </Typography>
            <Flex width="100%">
                <Flex direction="row" alignItems="center">
                    <Typography variant="body" weight="bold">
                        {t('Аккаунт в Директе')}
                    </Typography>
                    <WithHint
                        ml={0.5}
                        iconColor="var(--color-secondary)"
                        message={t(
                            'В случае необходимости сменить \nлогин в Директе \nобратитесь в поддержку',
                        )}
                    />
                </Flex>
                <Typography variant="body" pt={1}>
                    {login}
                </Typography>
            </Flex>
            <Flex width="100%" mt={2}>
                <Typography variant="body" weight="bold">
                    {t('Контент для продвижения:')}
                </Typography>
                <Typography variant="body" pt={1}>
                    {postsValue ? POSTS_TEXT_MAPPING[postsValue] : ''}
                </Typography>
            </Flex>
            {isStrategyEnabled ? (
                <Flex width="100%" mt={2}>
                    <Typography variant="body" weight="bold">
                        {t('Стратегия:')}
                    </Typography>
                    <Typography variant="body" pt={1}>
                        {strategyText}
                    </Typography>
                </Flex>
            ) : null}
            <Flex width="100%" mt={2}>
                <Typography variant="body" weight="bold">
                    {t('Целевые действия и их цена:')}
                </Typography>
                {selected?.length ? (
                    selected.map((goal) => {
                        const id = goal.id as keyof typeof selectedGoals;

                        return (
                            <Flex
                                key={goal.id}
                                mt={1}
                                direction="row"
                                width="100%"
                                justifyContent="space-between"
                            >
                                <Typography
                                    variant="body"
                                    textTransform="capitalize"
                                    overflow="hidden"
                                    textOverflow="ellipsis"
                                    noWrap
                                    flexShrink={1}
                                >
                                    {goal.name}
                                </Typography>
                                <NumberTypography variant="body" ml={1} flexShrink={0}>
                                    {getGoalCostText(selectedGoals?.[id], showCostType)}
                                </NumberTypography>
                            </Flex>
                        );
                    })
                ) : (
                    <Typography variant="body" pt={0.5}>
                        {t('Не заданы')}
                    </Typography>
                )}
            </Flex>
            <Flex width="100%" mt={2}>
                <Typography variant="body" weight="bold">
                    {t('Недельный бюджет:')}
                </Typography>
                {weeklyLimit ? (
                    <Price
                        priceInfo={{
                            integerPart: weeklyLimit,
                            currency: 'RUB',
                        }}
                        weight="regular"
                    />
                ) : (
                    <Typography variant="body" pt={0.5}>
                        {t('Не задан')}
                    </Typography>
                )}
            </Flex>
            <Flex mt={2} width="100%">
                <Typography variant="body" weight="bold">
                    {t('География показа:')}
                </Typography>
                <Typography variant="body" pt={1}>
                    {regionsValues ? regionsValues.join(', ') : 'Не выбрано'}
                </Typography>
            </Flex>
            <Flex mt={2}>
                <Typography variant="body" weight="bold">
                    {t('Дата начала:')}
                </Typography>
                <Typography variant="body" pt={1}>
                    {date ? formatDateToLocalStr(date) : t('Не задана')}
                </Typography>
            </Flex>
        </Wrapper>
    );
};
